import { useEffect, useRef, useState } from 'react';
import { computeBeatState, estimateBpmFromTap } from '../engine/beatSystem';
import { activeVisualEvent } from '../engine/timeline';
import { prepareTrack } from '../engine/trackPreparation';
import { makeManualAudioProfile } from '../engine/spotifyAudioProfile';
import { usePartyStore } from '../store/partyStore';

const MIC_DURATION_MS = 60 * 60 * 1000;
const MIC_BPM = 128;
const MIN_MIC_BPM = 70;
const MAX_MIC_BPM = 180;
const MIN_BEAT_INTERVAL_MS = 60_000 / MAX_MIC_BPM;
const MAX_BEAT_INTERVAL_MS = 60_000 / MIN_MIC_BPM;
const MIC_VISUAL_UPDATE_MS = 1000 / 30;
const MIC_SIGNAL_STEP = 0.01;
const MIC_ALBUM_ART_URL = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 600">
  <defs>
    <linearGradient id="micBg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#050510"/>
      <stop offset="0.45" stop-color="#251065"/>
      <stop offset="1" stop-color="#00d7ff"/>
    </linearGradient>
    <radialGradient id="micGlow" cx="50%" cy="48%" r="48%">
      <stop offset="0" stop-color="#ffffff" stop-opacity="0.85"/>
      <stop offset="1" stop-color="#00d7ff" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="600" height="600" fill="url(#micBg)"/>
  <circle cx="300" cy="292" r="250" fill="url(#micGlow)"/>
  <rect x="236" y="112" width="128" height="246" rx="64" fill="#f5fbff" opacity="0.92"/>
  <rect x="276" y="384" width="48" height="88" rx="22" fill="#f5fbff" opacity="0.92"/>
  <path d="M178 274 C178 376 422 376 422 274" fill="none" stroke="#f5fbff" stroke-width="28" stroke-linecap="round" opacity="0.92"/>
  <path d="M220 492 L380 492" stroke="#f5fbff" stroke-width="30" stroke-linecap="round" opacity="0.92"/>
  <path d="M112 404 C178 330 230 478 300 388 C372 292 420 424 488 340" fill="none" stroke="#00e5ff" stroke-width="18" stroke-linecap="round" opacity="0.72"/>
</svg>
`)}`;

let primedAudioContext: AudioContext | null = null;

export async function primeMicAudioContext() {
  const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextCtor) return;
  if (!primedAudioContext || primedAudioContext.state === 'closed') {
    primedAudioContext = new AudioContextCtor();
  }
  if (primedAudioContext.state === 'suspended') {
    await primedAudioContext.resume();
  }
}

export async function getMicAudioContext(): Promise<AudioContext> {
  const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error('Web Audio is not available in this browser.');
  }
  const audioContext =
    primedAudioContext && primedAudioContext.state !== 'closed'
      ? primedAudioContext
      : new AudioContextCtor();
  primedAudioContext = null;
  if (audioContext.state === 'suspended') await audioContext.resume();
  return audioContext;
}

function micIntensityFromEnergy(energy: number, onset: number): number {
  return Math.max(0.25, Math.min(1.25, 0.34 + energy * 1.7 + onset * 2.2));
}

function quantizeMicValue(value: number): number {
  return Math.round(value / MIC_SIGNAL_STEP) * MIC_SIGNAL_STEP;
}

function canonicalBeatInterval(intervalMs: number): number | null {
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) return null;
  let interval = intervalMs;
  if (interval > 720) interval /= 2;
  if (interval < 320) interval *= 2;
  if (interval < MIN_BEAT_INTERVAL_MS || interval > MAX_BEAT_INTERVAL_MS) return null;
  return interval;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function micErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return 'Could not start microphone input.';
  if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
    return 'Microphone permission was blocked. Allow microphone access for this site, then try Mic Mode again.';
  }
  if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
    return 'No microphone was found. Connect an input device, then try Mic Mode again.';
  }
  if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
    return 'The microphone is already in use by another app or browser tab.';
  }
  return err.message || 'Could not start microphone input.';
}

export function useMicPartySync(enabled: boolean) {
  const [error, setError] = useState('');
  const rafRef = useRef<number | undefined>(undefined);
  const startRef = useRef(0);
  const beatIndexRef = useRef(-1);
  const lastBeatAtRef = useRef(0);
  const lastOnsetAtRef = useRef(0);
  const lastVisualUpdateAtRef = useRef(0);
  const estimatedBpmRef = useRef(MIC_BPM);
  const beatAnchorMsRef = useRef(0);
  const beatIntervalsRef = useRef<number[]>([]);
  const smoothedEnergyRef = useRef(0);
  const baselineEnergyRef = useRef(0.02);
  const previousBassRef = useRef(0);
  const sourceNodesRef = useRef<{
    audioContext: AudioContext;
    stream: MediaStream;
    source: MediaStreamAudioSourceNode;
    analyser: AnalyserNode;
    timeData: Uint8Array<ArrayBuffer>;
    freqData: Uint8Array<ArrayBuffer>;
  } | null>(null);

  const {
    setPlayback,
    setCurrentTrack,
    setNextTrack,
    setQueueTracks,
    markTrackPrepared,
    setSpotifyDevice,
    setSyncStatus,
    setActiveEvent,
    setDisplayLyric,
    setLyricsStatus,
    setRunning,
    setMicSignal,
    visualStyle,
  } = usePartyStore();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let alive = true;
    setError('');
    setMicSignal(0, 0);
    startRef.current = performance.now();
    beatIndexRef.current = -1;
    lastBeatAtRef.current = 0;
    lastOnsetAtRef.current = 0;
    lastVisualUpdateAtRef.current = 0;
    estimatedBpmRef.current = MIC_BPM;
    beatAnchorMsRef.current = 0;
    beatIntervalsRef.current = [];
    smoothedEnergyRef.current = 0;
    baselineEnergyRef.current = 0.02;
    previousBassRef.current = 0;

    async function startMic() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Microphone input is not available in this browser.');
        }

        setSyncStatus('polling');
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        });
        if (!alive) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const audioContext = await getMicAudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.74;
        source.connect(analyser);

        sourceNodesRef.current = {
          audioContext,
          stream,
          source,
          analyser,
          timeData: new Uint8Array(new ArrayBuffer(analyser.fftSize)),
          freqData: new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount)),
        };

        setSpotifyDevice('Microphone', 'Live Input');
        setSyncStatus('synced');
        setMicSignal(0.04, 0);
        setLyricsStatus('disabled');
        setNextTrack(null);
        setQueueTracks([]);

      } catch (err) {
        if (!alive) return;
        setError(micErrorMessage(err));
        setSyncStatus('error');
        setRunning(false);
      }
    }

    startMic();

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const nodes = sourceNodesRef.current;
      const state = usePartyStore.getState();
      const progressMs = (performance.now() - startRef.current) % MIC_DURATION_MS;
      const hasManualTempo = Boolean(state.manualBpm || state.tapTimes.length >= 2);

      let energy = 0;
      let onset = 0;
      let bass = 0;
      if (nodes) {
        nodes.analyser.getByteTimeDomainData(nodes.timeData);
        nodes.analyser.getByteFrequencyData(nodes.freqData);

        let rms = 0;
        for (let i = 0; i < nodes.timeData.length; i++) {
          const v = (nodes.timeData[i] - 128) / 128;
          rms += v * v;
        }
        rms = Math.sqrt(rms / nodes.timeData.length);

        const bassBins = Math.min(18, nodes.freqData.length);
        for (let i = 0; i < bassBins; i++) bass += nodes.freqData[i] / 255;
        bass /= bassBins;

        energy = Math.min(1, rms * 4.8 + bass * 0.42);
        baselineEnergyRef.current = baselineEnergyRef.current * 0.985 + energy * 0.015;
        const energyOnset = Math.max(0, energy - baselineEnergyRef.current);
        const bassOnset = Math.max(0, bass - previousBassRef.current);
        onset = Math.min(1, energyOnset + bassOnset * 0.75);
        previousBassRef.current = previousBassRef.current * 0.52 + bass * 0.48;
        smoothedEnergyRef.current = smoothedEnergyRef.current * 0.82 + energy * 0.18;
        setMicSignal(quantizeMicValue(smoothedEnergyRef.current), quantizeMicValue(onset));
      }

      const now = performance.now();
      const currentEstimatedBeatMs = 60_000 / estimatedBpmRef.current;
      const onsetThreshold = Math.max(0.055, baselineEnergyRef.current * 0.55);
      const micBeat = !hasManualTempo
        && onset > onsetThreshold
        && smoothedEnergyRef.current > 0.035
        && now - lastBeatAtRef.current > Math.max(170, currentEstimatedBeatMs * 0.38);

      if (micBeat) {
        const interval = lastOnsetAtRef.current ? now - lastOnsetAtRef.current : 0;
        const canonicalInterval = canonicalBeatInterval(interval);
        if (canonicalInterval) {
          beatIntervalsRef.current.push(canonicalInterval);
          if (beatIntervalsRef.current.length > 10) beatIntervalsRef.current.shift();
          const learnedInterval = median(beatIntervalsRef.current);
          if (learnedInterval > 0) {
            const learnedBpm = Math.max(MIN_MIC_BPM, Math.min(MAX_MIC_BPM, Math.round(60_000 / learnedInterval)));
            estimatedBpmRef.current = estimatedBpmRef.current * 0.78 + learnedBpm * 0.22;
          }
        }
        lastOnsetAtRef.current = now;
        lastBeatAtRef.current = now;
        beatAnchorMsRef.current = progressMs;
      }

      const bpm = state.manualBpm
        ?? (state.tapTimes.length >= 2 ? estimateBpmFromTap(state.tapTimes) : Math.round(estimatedBpmRef.current));
      const beatMs = 60_000 / bpm;
      const beatOffsetMs = hasManualTempo ? 0 : beatAnchorMsRef.current % beatMs;
      const metronomeBeat = computeBeatState(
        progressMs,
        { bpm, manualTapTimes: state.tapTimes, beatOffsetMs },
        beatIndexRef.current
      );

      const beat = {
        ...metronomeBeat,
        isBeat: metronomeBeat.isBeat || micBeat,
        beatStrength: Math.max(metronomeBeat.beatStrength, 0.5 + smoothedEnergyRef.current * 2 + onset * 6),
      };
      if (beat.isBeat) beatIndexRef.current = beat.beatIndex;

      const track = state.currentTrack;
      let activeEvent = track ? activeVisualEvent(track.visualTimeline, progressMs) : null;
      const shouldPublishVisualState = now - lastVisualUpdateAtRef.current >= MIC_VISUAL_UPDATE_MS || beat.isBeat;
      if (!shouldPublishVisualState) return;
      lastVisualUpdateAtRef.current = now;

      if (state.sceneOverride && progressMs < state.sceneOverride.endMs) {
        activeEvent = state.sceneOverride;
      } else if (state.sceneOverride && progressMs >= state.sceneOverride.endMs) {
        usePartyStore.setState({ sceneOverride: null });
      }

      if (activeEvent && state.overrides.sceneLocked && state.overrides.forcedSceneId && activeEvent.sceneId !== state.overrides.forcedSceneId) {
        activeEvent = { ...activeEvent, sceneId: state.overrides.forcedSceneId };
      }

      if (activeEvent) {
        const micEnergy = quantizeMicValue(smoothedEnergyRef.current);
        const micOnset = quantizeMicValue(onset);
        activeEvent = {
          ...activeEvent,
          intensity: quantizeMicValue(micIntensityFromEnergy(micEnergy, micOnset)),
          params: {
            ...activeEvent.params,
            micEnergy,
            micOnset,
          },
        };
      }

      setPlayback(progressMs, true, beat);
      setActiveEvent(activeEvent);
      setDisplayLyric('');
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      alive = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const nodes = sourceNodesRef.current;
      if (nodes) {
        nodes.source.disconnect();
        nodes.stream.getTracks().forEach((track) => track.stop());
        nodes.audioContext.close().catch(() => {});
        sourceNodesRef.current = null;
      }
      setMicSignal(0, 0);
    };
  }, [
    enabled,
    setActiveEvent,
    setDisplayLyric,
    setLyricsStatus,
    setMicSignal,
    setNextTrack,
    setPlayback,
    setQueueTracks,
    setRunning,
    setSpotifyDevice,
    setSyncStatus,
  ]);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    prepareTrack(
      {
        id: `mic-live-${visualStyle}`,
        title: 'Microphone Input',
        artist: 'Live Audio',
        album: 'Mic Mode',
        durationMs: MIC_DURATION_MS,
        albumArtUrl: MIC_ALBUM_ART_URL,
        bpm: MIC_BPM,
        audioProfile: makeManualAudioProfile(MIC_BPM, 'mic-estimated'),
      },
      visualStyle,
      { lazy: true }
    ).then((track) => {
      if (!alive) return;
      setCurrentTrack({ ...track, lyrics: [], hooks: [], hasLyrics: false });
      markTrackPrepared(track.spotifyTrackId);
      setLyricsStatus('disabled');
    }).catch(() => {
      if (!alive) return;
      setSyncStatus('error');
    });

    return () => {
      alive = false;
    };
  }, [enabled, visualStyle, markTrackPrepared, setCurrentTrack, setLyricsStatus, setSyncStatus]);

  return { error };
}
